import { createFileRoute } from "@tanstack/react-router"
import { SavedPlate } from "ipctg-inflow-types/InFlowDB"
import { useState } from "react"
import { Form } from "react-aria-components"

import { CodeBlock } from "@/components/dev/CodeBlock"
import { PropsTable } from "@/components/dev/PropsTable"
import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Description from "@/globalComponents/atoms/Description"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import VehicleSelectorField from "@/globalComponents/molecules/VehicleSelectorField"

export const Route = createFileRoute("/qr/$qrLookupID/dev/molecules/forms")({
  staticData: {
    pageName: "Form Components",
  },
  component: RouteComponent,
})

function RouteComponent() {
  const [textValue, setTextValue] = useState("")
  const [emailValue, setEmailValue] = useState("")
  const [passwordValue, setPasswordValue] = useState("")
  const [phoneValue, setPhoneValue] = useState("")
  const [phoneWithCountryValue, setPhoneWithCountryValue] = useState("")
  const [vehicleValue, setVehicleValue] = useState("")
  const [showError, setShowError] = useState(false)

  const vehicles: SavedPlate[] = [
    {
      plate: "ABC123",
      nickname: "My Honda",
      state: "CA",
      lastUsed: new Date().toISOString(),
    },
    {
      plate: "XYZ789",
      nickname: "",
      state: "NY",
      lastUsed: new Date().toISOString(),
    },
    {
      plate: "DEF456",
      nickname: "Work Truck",
      state: "TX",
      lastUsed: new Date().toISOString(),
    },
  ]

  return (
    <>
      <style>{`
        .dev-container > *:not(:first-child) {
          margin-top: var(--space-5);
        }
      `}</style>
      <div className="dev-container">
        <Card>
          <Heading level={1} as={2}>
            Form Components
          </Heading>
          <Text>
            Form components built on React Aria Components provide accessible,
            interactive form fields with validation, labels, descriptions, and
            error messages. This page covers TextField and VehicleSelectorField
            components that work together to create complete form experiences.
          </Text>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            TextField Component
          </Heading>
          <Text>
            The TextField component is a wrapper around React Aria's TextField
            that provides consistent styling. It includes multiple input
            variants for different data types.
          </Text>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-4)",
            }}
          >
            <div>
              <Text level={2} className="semi-bold">
                Basic Text Input
              </Text>
              <Text level={3}>
                Standard text field with label and description
              </Text>
              <TextField>
                <Label>Full Name</Label>
                <Description>Enter your first and last name</Description>
                <TextField.Input
                  value={textValue}
                  onChange={(e) => setTextValue(e.target.value)}
                />
              </TextField>
              <Text level={3} style={{ marginTop: "var(--space-2)" }}>
                Current value: {textValue || "(empty)"}
              </Text>
              <CodeBlock>{`<TextField>
  <Label>Full Name</Label>
  <Description>Enter your first and last name</Description>
  <TextField.Input
    value={textValue}
    onChange={(e) => setTextValue(e.target.value)}
  />
</TextField>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Email Input
              </Text>
              <Text level={3}>Input with email type and validation</Text>
              <TextField>
                <Label>Email Address</Label>
                <TextField.Input
                  type="email"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  placeholder="you@example.com"
                />
              </TextField>
              <CodeBlock>{`<TextField>
  <Label>Email Address</Label>
  <TextField.Input
    type="email"
    value={emailValue}
    onChange={(e) => setEmailValue(e.target.value)}
    placeholder="you@example.com"
  />
</TextField>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Password Input
              </Text>
              <Text level={3}>Password field with show/hide toggle button</Text>
              <TextField>
                <Label>Password</Label>
                <Description>Must be at least 8 characters</Description>
                <TextField.Password
                  innerRef={() => {}}
                  value={passwordValue}
                  onChange={(e) => setPasswordValue(e.target.value)}
                />
              </TextField>
              <CodeBlock>{`<TextField>
  <Label>Password</Label>
  <Description>Must be at least 8 characters</Description>
  <TextField.Password
    innerRef={() => {}}
    value={passwordValue}
    onChange={(e) => setPasswordValue(e.target.value)}
  />
</TextField>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Phone Input
              </Text>
              <Text level={3}>Formatted phone number input (US format)</Text>
              <TextField>
                <Label>Phone Number</Label>
                <TextField.Phone
                  innerRef={() => {}}
                  value={phoneValue}
                  onChange={(e) => setPhoneValue(e.target.value)}
                />
              </TextField>
              <CodeBlock>{`<TextField>
  <Label>Phone Number</Label>
  <TextField.Phone
    innerRef={() => {}}
    value={phoneValue}
    onChange={(e) => setPhoneValue(e.target.value)}
  />
</TextField>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Phone with Country Select
              </Text>
              <Text level={3}>Phone input with country code selector</Text>
              <TextField>
                <Label>International Phone</Label>
                <TextField.PhoneWithCountrySelect
                  innerRef={() => {}}
                  value={phoneWithCountryValue}
                  onChange={(e) => setPhoneWithCountryValue(e.target.value)}
                  placeholder="555-123-4567"
                />
              </TextField>
              <CodeBlock>{`<TextField>
  <Label>International Phone</Label>
  <TextField.PhoneWithCountrySelect
    innerRef={() => {}}
    value={phoneWithCountryValue}
    onChange={(e) => setPhoneWithCountryValue(e.target.value)}
    placeholder="555-123-4567"
  />
</TextField>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Field with Error
              </Text>
              <Text level={3}>Showing validation error state</Text>
              <TextField isInvalid={showError}>
                <Label>Username</Label>
                <TextField.Input value="invalid@user" />
                {showError && (
                  <FieldError>
                    Username cannot contain special characters
                  </FieldError>
                )}
              </TextField>
              <Button
                buttonType="primary"
                presentation="ghost"
                onPress={() => setShowError(!showError)}
                style={{ marginTop: "var(--space-2)" }}
              >
                Toggle Error
              </Button>
              <CodeBlock>{`<TextField isInvalid={showError}>
  <Label>Username</Label>
  <TextField.Input value="invalid@user" />
  {showError && (
    <FieldError>
      Username cannot contain special characters
    </FieldError>
  )}
</TextField>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Required Field
              </Text>
              <Text level={3}>Field marked as required</Text>
              <TextField isRequired>
                <Label>Required Field</Label>
                <Description>This field must be filled out</Description>
                <TextField.Input />
              </TextField>
              <CodeBlock>{`<TextField isRequired>
  <Label>Required Field</Label>
  <Description>This field must be filled out</Description>
  <TextField.Input />
</TextField>`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Disabled Field
              </Text>
              <TextField isDisabled>
                <Label>Disabled Field</Label>
                <TextField.Input value="Cannot edit this" />
              </TextField>
              <CodeBlock>{`<TextField isDisabled>
  <Label>Disabled Field</Label>
  <TextField.Input value="Cannot edit this" />
</TextField>`}</CodeBlock>
            </div>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            VehicleSelectorField Component
          </Heading>
          <Text>
            A specialized radio group component for selecting vehicles from a
            saved list. This component is commonly used in parking flows.
          </Text>

          <div>
            <Text level={2} className="semi-bold">
              Vehicle Selection
            </Text>
            <Text level={3}>Select a vehicle or choose to enter a new one</Text>
            <VehicleSelectorField
              value={vehicleValue}
              onChange={setVehicleValue}
            >
              <Label>Select Vehicle</Label>
              {vehicles.map((vehicle) => (
                <VehicleSelectorField.Radio
                  key={vehicle.plate}
                  vehicle={vehicle || undefined}
                />
              ))}
              <VehicleSelectorField.Radio vehicle={undefined} />
            </VehicleSelectorField>
            <Text level={3} style={{ marginTop: "var(--space-2)" }}>
              Selected: {vehicleValue || "(enter new plate)"}
            </Text>
            <CodeBlock>{`const vehicles: SavedPlate[] = [
  { plate: "ABC123", nickname: "My Honda", state: "CA", lastUsed: "..." },
  { plate: "XYZ789", nickname: "", state: "NY", lastUsed: "..." },
  { plate: "DEF456", nickname: "Work Truck", state: "TX", lastUsed: "..." },
]

<VehicleSelectorField
  value={vehicleValue}
  onChange={setVehicleValue}
>
  <Label>Select Vehicle</Label>
  {vehicles.map((vehicle) => (
    <VehicleSelectorField.Radio
      key={vehicle.plate}
      vehicle={vehicle}
    />
  ))}
  <VehicleSelectorField.Radio vehicle={undefined} />
</VehicleSelectorField>`}</CodeBlock>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Complete Form Example
          </Heading>
          <Text>
            Example of multiple form components working together in a complete
            form
          </Text>
          <Form
            onSubmit={(e) => {
              e.preventDefault()
              alert("Form submitted!")
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--space-4)",
              }}
            >
              <TextField isRequired>
                <Label>Full Name</Label>
                <TextField.Input placeholder="John Doe" />
              </TextField>

              <TextField isRequired>
                <Label>Email</Label>
                <TextField.Input type="email" placeholder="john@example.com" />
              </TextField>

              <TextField>
                <Label>Phone</Label>
                <TextField.Phone innerRef={() => {}} />
              </TextField>

              <Button type="submit" buttonType="primary">
                Submit Form
              </Button>
            </div>
          </Form>
          <CodeBlock>{`<Form onSubmit={(e) => {
  e.preventDefault()
  alert("Form submitted!")
}}>
  <TextField isRequired>
    <Label>Full Name</Label>
    <TextField.Input placeholder="John Doe" />
  </TextField>

  <TextField isRequired>
    <Label>Email</Label>
    <TextField.Input type="email" placeholder="john@example.com" />
  </TextField>

  <TextField>
    <Label>Phone</Label>
    <TextField.Phone innerRef={() => {}} />
  </TextField>

  <Button type="submit" buttonType="primary">
    Submit Form
  </Button>
</Form>`}</CodeBlock>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            TextField Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "isRequired",
                type: "boolean",
                default: "false",
                description: "Whether the field is required",
              },
              {
                name: "isDisabled",
                type: "boolean",
                default: "false",
                description: "Whether the field is disabled",
              },
              {
                name: "isInvalid",
                type: "boolean",
                default: "false",
                description: "Whether the field has a validation error",
              },
              {
                name: "className",
                type: "string",
                default: "undefined",
                description: "Additional CSS classes",
              },
            ]}
          />
          <Text>
            Note: TextField extends React Aria Components TextFieldProps and
            supports all standard props including validation, change handlers,
            and more.
          </Text>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            TextField.Input Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "value",
                type: "string",
                default: "undefined",
                description: "Controlled input value",
              },
              {
                name: "onChange",
                type: "(e: ChangeEvent) => void",
                default: "undefined",
                description: "Change event handler",
              },
              {
                name: "placeholder",
                type: "string",
                default: "undefined",
                description: "Placeholder text",
              },
              {
                name: "type",
                type: '"text" | "email" | "tel" | etc.',
                default: '"text"',
                description: "HTML input type",
              },
              {
                name: "innerRef",
                type: "RefCallBack",
                default: "undefined",
                description: "Ref for react-hook-form integration",
              },
            ]}
          />
        </Card>

        <Card>
          <Heading level={2} as={3}>
            VehicleSelectorField Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "value",
                type: "string",
                default: "undefined",
                description: "Selected vehicle plate number",
              },
              {
                name: "onChange",
                type: "(value: string) => void",
                default: "undefined",
                description: "Selection change handler",
              },
            ]}
          />
          <Text>
            VehicleSelectorField.Radio requires a <code>vehicle</code> prop of
            type <code>SavedPlate | undefined</code>. Pass{" "}
            <code>undefined</code> to render the "enter new plate" option.
          </Text>
        </Card>
      </div>
    </>
  )
}
